import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from './firebase';
import {
  collection, query, onSnapshot, doc, addDoc, updateDoc, serverTimestamp
} from 'firebase/firestore';
import { useRole } from './RoleContext';
import { Briefcase, X, User, Trash2, Check, UserCog } from 'lucide-react';
import PublicHolidays from './PublicHolidays';
import HRCalendar from './HRCalendar';
import Payroll from './Payroll';
import UserRoles from './UserRoles';
import { deleteToRecycleBin } from './recycleBinUtil';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${String(d.getDate()).padStart(2, '0')}-${d.toLocaleString('en-US', { month: 'short' })}-${d.getFullYear()}`;
};

// Inclusive day count between two YYYY-MM-DD dates.
const daysBetween = (from, to) => {
  const a = new Date(from), b = new Date(to);
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return 0;
  return Math.max(1, Math.round((b - a) / 86400000) + 1);
};

const getTodayString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Bangladesh Labour Act, 2006 standard annual entitlements for a permanent
// worker: 14 days Sick Leave, 10 days Casual Leave, and Earned/Annual Leave
// accrued at roughly 1 day per 18 worked (~16 days/year for a ~300-day work
// year); Maternity Leave (16 weeks / 112 days) applies only to female
// employees. These are the defaults when an employee has no quota explicitly
// set -- Unpaid leave has no quota, it just doesn't get paid.
const DEFAULT_LEAVE_QUOTA = { Sick: 14, Casual: 10, Earned: 16, Maternity: 112 };
const BASE_QUOTA_LEAVE_TYPES = ['Sick', 'Casual', 'Earned'];
const quotaTypesFor = (emp) => emp?.gender === 'Female' ? [...BASE_QUOTA_LEAVE_TYPES, 'Maternity'] : BASE_QUOTA_LEAVE_TYPES;
const leaveTypesFor = (emp) => emp?.gender === 'Female' ? ['Sick', 'Casual', 'Earned', 'Maternity', 'Unpaid'] : ['Sick', 'Casual', 'Earned', 'Unpaid'];

const Avatar = ({ url }) => url ? (
  <img src={url} alt="" style={{ width: '34px', height: '34px', borderRadius: '50%', objectFit: 'cover' }} />
) : (
  <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: '#f4f7fe', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4a00e0' }}>
    <User size={16} />
  </div>
);

const getCurrentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const getTodayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const HR = () => {
  const { isFullAccess: admin } = useRole();
  const [employees, setEmployees] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [activeTab, setActiveTab] = useState('employees'); // 'employees' | 'leave' | 'holidays' | 'calendar' | 'payroll'
  const [leaveSubTab, setLeaveSubTab] = useState('summary'); // 'summary' | 'requests'
  const [requestFilter, setRequestFilter] = useState('Pending'); // 'All' | 'Pending' | 'Approved' | 'This Month' | 'Today'
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('All');

  const [quotaEmployee, setQuotaEmployee] = useState(null);
  const [quotaInput, setQuotaInput] = useState(DEFAULT_LEAVE_QUOTA);


  const [leaveEmployee, setLeaveEmployee] = useState(null);
  const [leaveForm, setLeaveForm] = useState({});

  const [historyEmployee, setHistoryEmployee] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'employees')), (snap) => {
      setEmployees(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'leaveRecords')), (snap) => {
      setLeaves(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  const activeEmployees = useMemo(
    () => employees.filter((e) => e.active !== false).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [employees]
  );
  const terminatedEmployees = useMemo(
    () => employees.filter((e) => e.active === false),
    [employees]
  );

  const departments = useMemo(
    () => Array.from(new Set(employees.map((e) => e.department).filter(Boolean))).sort(),
    [employees]
  );

  const leavesThisMonth = useMemo(() => {
    const month = getCurrentMonth();
    return leaves.filter((l) => (l.fromDate || '').startsWith(month)).length;
  }, [leaves]);

  const filteredEmployees = useMemo(() => {
    const term = search.trim().toLowerCase();
    return employees
      .filter((e) => departmentFilter === 'All' || e.department === departmentFilter)
      .filter((e) => !term || (e.name || '').toLowerCase().includes(term) || (e.phone || '').includes(term))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [employees, search, departmentFilter]);

  const leaveSummary = useMemo(() => {
    const map = {};
    employees.forEach((e) => {
      const empLeaves = leaves.filter((l) => l.employeeId === e.id);
      const breakdown = {};
      const quotaTypes = quotaTypesFor(e);
      quotaTypes.forEach((type) => {
        const taken = empLeaves.filter((l) => l.type === type).reduce((s, l) => s + (l.days || 0), 0);
        const quota = Number(e.leaveQuota?.[type]) || DEFAULT_LEAVE_QUOTA[type];
        breakdown[type] = { taken, quota, remaining: Math.max(0, quota - taken) };
      });
      const totalTaken = quotaTypes.reduce((s, t) => s + breakdown[t].taken, 0);
      map[e.id] = { breakdown, totalTaken, count: empLeaves.length };
    });
    return map;
  }, [employees, leaves]);

  const openQuota = (emp) => {
    setQuotaEmployee(emp);
    const input = {};
    quotaTypesFor(emp).forEach((type) => { input[type] = emp.leaveQuota?.[type] ?? DEFAULT_LEAVE_QUOTA[type]; });
    setQuotaInput(input);
  };

  const saveQuota = async () => {
    if (!admin || !quotaEmployee) return;
    const leaveQuota = {};
    quotaTypesFor(quotaEmployee).forEach((type) => { leaveQuota[type] = Number(quotaInput[type]) || 0; });
    await updateDoc(doc(db, 'employees', quotaEmployee.id), {
      leaveQuota
    });
    setQuotaEmployee(null);
  };

  const openAddLeave = (emp) => {
    setLeaveEmployee(emp);
    setLeaveForm({ fromDate: getTodayString(), toDate: getTodayString(), type: 'Sick', reason: '' });
  };

  const saveLeave = async () => {
    if (!admin || !leaveEmployee) return;
    if (!leaveForm.fromDate || !leaveForm.toDate) return alert('Please pick both dates.');
    if (leaveForm.toDate < leaveForm.fromDate) return alert('"To" date cannot be before "From" date.');
    try {
      await addDoc(collection(db, 'leaveRecords'), {
        employeeId: leaveEmployee.id,
        employeeName: leaveEmployee.name,
        fromDate: leaveForm.fromDate,
        toDate: leaveForm.toDate,
        days: daysBetween(leaveForm.fromDate, leaveForm.toDate),
        type: leaveForm.type,
        reason: leaveForm.reason.trim(),
        status: 'Pending',
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || ''
      });
      setLeaveEmployee(null);
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const [approvingLeave, setApprovingLeave] = useState(null);
  const [approvalNote, setApprovalNote] = useState('');

  const openApprove = (leaveRecord) => {
    setApprovingLeave(leaveRecord);
    setApprovalNote('');
  };

  const confirmApprove = async () => {
    if (!admin || !approvingLeave) return;
    await updateDoc(doc(db, 'leaveRecords', approvingLeave.id), { status: 'Approved', approvalNote: approvalNote.trim() });
    setApprovingLeave(null);
  };

  const deleteLeave = async (leaveRecord) => {
    if (!admin) return;
    if (!window.confirm(`Delete this ${leaveRecord.type} leave record (${formatDate(leaveRecord.fromDate)} - ${formatDate(leaveRecord.toDate)})?`)) return;
    await deleteToRecycleBin({ collectionName: 'leaveRecords', id: leaveRecord.id, data: leaveRecord, label: 'Leave Record' });
  };

  const historyForEmployee = (empId) => leaves
    .filter((l) => l.employeeId === empId)
    .sort((a, b) => (b.fromDate || '').localeCompare(a.fromDate || ''));

  const filteredRequests = useMemo(() => {
    const month = getCurrentMonth();
    const today = getTodayIso();
    let list = leaves;
    if (requestFilter === 'Pending') list = list.filter((l) => (l.status || 'Pending') === 'Pending');
    else if (requestFilter === 'Approved') list = list.filter((l) => l.status === 'Approved');
    else if (requestFilter === 'This Month') list = list.filter((l) => l.status === 'Approved' && l.fromDate <= `${month}-31` && l.toDate >= `${month}-01`);
    else if (requestFilter === 'Today') list = list.filter((l) => l.status === 'Approved' && l.fromDate <= today && l.toDate >= today);
    return list.slice().sort((a, b) => (b.fromDate || '').localeCompare(a.fromDate || ''));
  }, [leaves, requestFilter]);

  return (
    <div style={{ padding: '20px', background: '#f4f7fe', minHeight: '100vh' }}>
      <div className="white-box" style={{ padding: '30px', borderRadius: '25px', background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>

        <h2 style={{ margin: '0 0 20px', color: '#333' }}><Briefcase size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />HR</h2>

        {!admin && (
          <div style={{ background: '#fff3cd', color: '#8a6d3b', padding: '10px 15px', borderRadius: '10px', marginBottom: '20px', fontSize: '13px' }}>
            Only the Admin account can set leave quotas or add/delete leave records. You have view-only access.
          </div>
        )}

        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '25px' }}>
          <div style={{ background: 'linear-gradient(135deg, #4a00e0, #7b2ff7)', padding: '20px', borderRadius: '20px', boxShadow: '0 8px 20px rgba(74,0,224,0.25)', minWidth: '200px', flex: 1.4 }}>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.85)', fontSize: '13px', fontWeight: 'bold' }}>Total Employees</p>
            <h2 style={{ margin: 0, color: '#fff', fontSize: '28px' }}>{employees.length}</h2>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '11px' }}>{activeEmployees.length} active, {terminatedEmployees.length} terminated</p>
          </div>
          <div className="hover-lift" style={{ background: '#fff', padding: '20px', borderRadius: '20px', borderLeft: '6px solid #10b981', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', minWidth: '160px', flex: 1 }}>
            <p style={{ margin: 0, color: '#666', fontSize: '13px', fontWeight: 'bold' }}>Active Employees</p>
            <h2 style={{ margin: 0, color: '#10b981', fontSize: '22px' }}>{activeEmployees.length}</h2>
          </div>
          <div className="hover-lift" style={{ background: '#fff', padding: '20px', borderRadius: '20px', borderLeft: '6px solid #999', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', minWidth: '160px', flex: 1 }}>
            <p style={{ margin: 0, color: '#666', fontSize: '13px', fontWeight: 'bold' }}>Terminated Employees</p>
            <h2 style={{ margin: 0, color: '#999', fontSize: '22px' }}>{terminatedEmployees.length}</h2>
          </div>
          <div className="hover-lift" style={{ background: '#fff', padding: '20px', borderRadius: '20px', borderLeft: '6px solid #f59e0b', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', minWidth: '160px', flex: 1 }}>
            <p style={{ margin: 0, color: '#666', fontSize: '13px', fontWeight: 'bold' }}>Leaves This Month</p>
            <h2 style={{ margin: 0, color: '#f59e0b', fontSize: '22px' }}>{leavesThisMonth}</h2>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '18px' }}>
          <button
            onClick={() => setActiveTab('employees')}
            style={{ padding: '10px 18px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', border: `1px solid ${activeTab === 'employees' ? '#4a00e0' : '#ddd'}`, background: activeTab === 'employees' ? '#4a00e0' : '#fff', color: activeTab === 'employees' ? '#fff' : '#333' }}
          >
            Employee List
          </button>
          <button
            onClick={() => setActiveTab('leave')}
            style={{ padding: '10px 18px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', border: `1px solid ${activeTab === 'leave' ? '#4a00e0' : '#ddd'}`, background: activeTab === 'leave' ? '#4a00e0' : '#fff', color: activeTab === 'leave' ? '#fff' : '#333' }}
          >
            Leave Records
          </button>
          <button
            onClick={() => setActiveTab('holidays')}
            style={{ padding: '10px 18px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', border: `1px solid ${activeTab === 'holidays' ? '#4a00e0' : '#ddd'}`, background: activeTab === 'holidays' ? '#4a00e0' : '#fff', color: activeTab === 'holidays' ? '#fff' : '#333' }}
          >
            Public Holidays
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            style={{ padding: '10px 18px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', border: `1px solid ${activeTab === 'calendar' ? '#4a00e0' : '#ddd'}`, background: activeTab === 'calendar' ? '#4a00e0' : '#fff', color: activeTab === 'calendar' ? '#fff' : '#333' }}
          >
            Calendar
          </button>
          <button
            onClick={() => setActiveTab('payroll')}
            style={{ padding: '10px 18px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', border: `1px solid ${activeTab === 'payroll' ? '#4a00e0' : '#ddd'}`, background: activeTab === 'payroll' ? '#4a00e0' : '#fff', color: activeTab === 'payroll' ? '#fff' : '#333' }}
          >
            Payroll
          </button>
          <button
            onClick={() => setActiveTab('roles')}
            style={{ padding: '10px 18px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', border: `1px solid ${activeTab === 'roles' ? '#4a00e0' : '#ddd'}`, background: activeTab === 'roles' ? '#4a00e0' : '#fff', color: activeTab === 'roles' ? '#fff' : '#333', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <UserCog size={14} /> User Roles
          </button>
        </div>

        {activeTab === 'employees' && (
          <>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
              <input
                type="text" placeholder="🔍 Search by Name / Phone..."
                value={search} onChange={(e) => setSearch(e.target.value)}
                className="input-field" style={{ width: '260px', marginBottom: 0 }}
              />
              <select className="input-field" value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)} style={{ width: '200px', marginBottom: 0 }}>
                <option value="All">All Departments</option>
                {departments.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #f4f7fe', color: '#888', textAlign: 'left' }}>
                  <th style={{ padding: '15px' }}>Name</th>
                  <th>Role</th>
                  <th>Department</th>
                  <th>Employment Type</th>
                  <th>Phone</th>
                  <th>Joining Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((emp) => (
                  <tr key={emp.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
                    <td style={{ padding: '15px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Avatar url={emp.photoUrl} />
                        <b>{emp.name}</b>
                      </div>
                    </td>
                    <td>{emp.position || '-'}</td>
                    <td>{emp.department || '-'}</td>
                    <td>{emp.employmentType || 'Full-Time'}</td>
                    <td>{emp.phone || '-'}</td>
                    <td>{emp.joiningDate ? formatDate(emp.joiningDate) : '-'}</td>
                    <td>
                      {emp.active === false ? (
                        <span style={{ padding: '4px 10px', background: '#f4f4f4', color: '#999', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold' }}>Terminated {emp.terminationDate ? `(${formatDate(emp.terminationDate)})` : ''}</span>
                      ) : (
                        <span style={{ padding: '4px 10px', background: '#eafff3', color: '#218c61', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold' }}>Active</span>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredEmployees.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: '30px', color: '#999' }}>No employees found -- add one from the Payroll page.</td></tr>
                )}
              </tbody>
            </table>
          </>
        )}

        {activeTab === 'leave' && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <button
              onClick={() => setLeaveSubTab('summary')}
              style={{ padding: '7px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', border: `1px solid ${leaveSubTab === 'summary' ? '#4a00e0' : '#ddd'}`, background: leaveSubTab === 'summary' ? '#4a00e0' : '#fff', color: leaveSubTab === 'summary' ? '#fff' : '#333' }}
            >
              By Employee
            </button>
            <button
              onClick={() => setLeaveSubTab('requests')}
              style={{ padding: '7px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', border: `1px solid ${leaveSubTab === 'requests' ? '#4a00e0' : '#ddd'}`, background: leaveSubTab === 'requests' ? '#4a00e0' : '#fff', color: leaveSubTab === 'requests' ? '#fff' : '#333' }}
            >
              Leave Requests
            </button>
          </div>
        )}

        {activeTab === 'leave' && leaveSubTab === 'requests' && (
          <>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {['Pending', 'Approved', 'This Month', 'Today', 'All'].map((f) => (
                <button
                  key={f}
                  onClick={() => setRequestFilter(f)}
                  style={{ padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', border: `1px solid ${requestFilter === f ? '#4a00e0' : '#ddd'}`, background: requestFilter === f ? '#4a00e0' : '#fff', color: requestFilter === f ? '#fff' : '#333' }}
                >
                  {f}
                </button>
              ))}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #f4f7fe', color: '#888', textAlign: 'left' }}>
                  <th style={{ padding: '15px' }}>Employee</th>
                  <th>Type</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Days</th>
                  <th>Reason</th>
                  <th>Status</th>
                  {admin && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((l) => (
                  <tr key={l.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
                    <td style={{ padding: '15px' }}><b>{l.employeeName}</b></td>
                    <td>{l.type}</td>
                    <td>{formatDate(l.fromDate)}</td>
                    <td>{formatDate(l.toDate)}</td>
                    <td>{l.days}</td>
                    <td style={{ fontSize: '12px', color: '#888' }}>{l.reason || '-'}</td>
                    <td>
                      <span style={{ padding: '3px 9px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold', background: l.status === 'Approved' ? '#eafff3' : '#fff3cd', color: l.status === 'Approved' ? '#218c61' : '#8a6d3b' }}>
                        {l.status || 'Pending'}
                      </span>
                      {l.approvalNote && <div style={{ fontSize: '10px', color: '#999', marginTop: '3px' }}>"{l.approvalNote}"</div>}
                    </td>
                    {admin && (
                      <td>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {(l.status || 'Pending') === 'Pending' && (
                            <button onClick={() => openApprove(l)} style={{ padding: '4px 10px', fontSize: '11px', borderRadius: '6px', border: 'none', background: '#10b981', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}><Check size={12} /> Approve</button>
                          )}
                          <button onClick={() => deleteLeave(l)} style={{ padding: '4px 8px', borderRadius: '6px', border: 'none', background: '#ffeded', color: 'red', cursor: 'pointer' }}><Trash2 size={13} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {filteredRequests.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: '30px', color: '#999' }}>No leave records match this filter.</td></tr>
                )}
              </tbody>
            </table>
          </>
        )}

        {activeTab === 'leave' && leaveSubTab === 'summary' && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f4f7fe', color: '#888', textAlign: 'left' }}>
                <th style={{ padding: '15px' }}>Name</th>
                <th>Leave Balance (Bangladesh Labour Act defaults)</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {activeEmployees.map((emp) => {
                const s = leaveSummary[emp.id] || { breakdown: {} };
                return (
                  <tr key={emp.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
                    <td style={{ padding: '15px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Avatar url={emp.photoUrl} />
                        <b>{emp.name}</b>
                      </div>
                    </td>
                    <td style={{ padding: '10px 0' }}>
                      <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
                        {quotaTypesFor(emp).map((type) => {
                          const b = s.breakdown[type] || { taken: 0, quota: DEFAULT_LEAVE_QUOTA[type], remaining: DEFAULT_LEAVE_QUOTA[type] };
                          const pct = Math.min(100, (b.taken / b.quota) * 100);
                          return (
                            <div key={type} style={{ width: '110px' }}>
                              <div style={{ fontSize: '11px', color: '#888', marginBottom: '3px' }}>{type}: {b.taken}/{b.quota}</div>
                              <div style={{ width: '100%', height: '6px', borderRadius: '4px', background: '#f0f0f0', overflow: 'hidden' }}>
                                <div style={{ width: `${pct}%`, height: '100%', background: b.taken >= b.quota ? '#e74c3c' : pct > 70 ? '#f59e0b' : '#10b981' }} />
                              </div>
                              <div style={{ fontSize: '10px', color: '#999' }}>{b.remaining} left</div>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {admin && <button onClick={() => openAddLeave(emp)} style={{ padding: '5px 10px', fontSize: '11px', borderRadius: '6px', border: 'none', background: '#4a00e0', color: '#fff', cursor: 'pointer' }}>Add Leave</button>}
                        {admin && <button onClick={() => openQuota(emp)} style={{ padding: '5px 10px', fontSize: '11px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Set Quota</button>}
                        <button onClick={() => setHistoryEmployee(emp)} style={{ padding: '5px 10px', fontSize: '11px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>History ({s.count || 0})</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {activeEmployees.length === 0 && (
                <tr><td colSpan={3} style={{ textAlign: 'center', padding: '30px', color: '#999' }}>No active employees yet -- add one from the Payroll page.</td></tr>
              )}
            </tbody>
          </table>
        )}

        {activeTab === 'holidays' && <PublicHolidays />}
        {activeTab === 'calendar' && <HRCalendar />}
        {activeTab === 'payroll' && <Payroll embedded />}
        {activeTab === 'roles' && <UserRoles />}
      </div>

      {quotaEmployee && (
        <div onClick={() => setQuotaEmployee(null)} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', padding: '30px', borderRadius: '20px', width: '340px', maxWidth: '95%', position: 'relative' }}>
            <button onClick={() => setQuotaEmployee(null)} style={{ position: 'absolute', top: '15px', right: '15px', border: 'none', background: '#ffeded', color: 'red', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer' }}><X size={16} /></button>
            <h3 style={{ marginTop: 0 }}>Leave Quota — {quotaEmployee.name}</h3>
            <p style={{ fontSize: '11px', color: '#999', marginTop: '-8px' }}>Defaults follow the Bangladesh Labour Act, 2006 -- change any of these only if this employee's entitlement is different.</p>
            {quotaTypesFor(quotaEmployee).map((type) => (
              <div key={type} style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '12px', color: '#888', fontWeight: 'bold' }}>{type} Leave (days/year)</label>
                <input className="input-field" type="number" value={quotaInput[type]} onChange={(e) => setQuotaInput({ ...quotaInput, [type]: e.target.value })} style={{ marginBottom: 0 }} />
              </div>
            ))}
            <button onClick={saveQuota} className="btn-primary" style={{ width: '100%', padding: '12px', marginTop: '5px' }}>Save</button>
          </div>
        </div>
      )}

      {approvingLeave && (
        <div onClick={() => setApprovingLeave(null)} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100, padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', padding: '25px', borderRadius: '20px', width: '360px', maxWidth: '95%' }}>
            <h3 style={{ marginTop: 0 }}>Approve Leave — {approvingLeave.employeeName}</h3>
            <p style={{ fontSize: '13px', color: '#666' }}>{approvingLeave.type} leave, {formatDate(approvingLeave.fromDate)} - {formatDate(approvingLeave.toDate)} ({approvingLeave.days} day{approvingLeave.days > 1 ? 's' : ''})</p>
            <input className="input-field" placeholder="Note (optional)" value={approvalNote} onChange={(e) => setApprovalNote(e.target.value)} />
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button onClick={() => setApprovingLeave(null)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button onClick={confirmApprove} className="btn-confirm" style={{ flex: 1, padding: '12px' }}>Approve</button>
            </div>
          </div>
        </div>
      )}

      {leaveEmployee && (
        <div onClick={() => setLeaveEmployee(null)} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', padding: '30px', borderRadius: '20px', width: '380px', maxWidth: '95%', position: 'relative' }}>
            <button onClick={() => setLeaveEmployee(null)} style={{ position: 'absolute', top: '15px', right: '15px', border: 'none', background: '#ffeded', color: 'red', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer' }}><X size={16} /></button>
            <h3 style={{ marginTop: 0 }}>Add Leave — {leaveEmployee.name}</h3>
            <label style={{ fontSize: '12px', color: '#888', fontWeight: 'bold' }}>From</label>
            <input className="input-field" type="date" value={leaveForm.fromDate || ''} onChange={(e) => setLeaveForm({ ...leaveForm, fromDate: e.target.value })} />
            <label style={{ fontSize: '12px', color: '#888', fontWeight: 'bold' }}>To</label>
            <input className="input-field" type="date" value={leaveForm.toDate || ''} onChange={(e) => setLeaveForm({ ...leaveForm, toDate: e.target.value })} />
            <select className="input-field" value={leaveForm.type || 'Sick'} onChange={(e) => setLeaveForm({ ...leaveForm, type: e.target.value })}>
              {leaveTypesFor(leaveEmployee).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input className="input-field" placeholder="Reason" value={leaveForm.reason || ''} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} />
            <button onClick={saveLeave} className="btn-confirm" style={{ width: '100%', padding: '12px', marginTop: '5px' }}>Save Leave</button>
          </div>
        </div>
      )}

      {historyEmployee && (
        <div onClick={() => setHistoryEmployee(null)} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', padding: '30px', borderRadius: '20px', width: '450px', maxWidth: '95%', maxHeight: '80vh', overflowY: 'auto', position: 'relative' }}>
            <button onClick={() => setHistoryEmployee(null)} style={{ position: 'absolute', top: '15px', right: '15px', border: 'none', background: '#ffeded', color: 'red', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer' }}><X size={16} /></button>
            <h3 style={{ marginTop: 0 }}>Leave History — {historyEmployee.name}</h3>
            {historyForEmployee(historyEmployee.id).length === 0 && (
              <p style={{ color: '#999', fontSize: '13px' }}>No leave records yet.</p>
            )}
            {historyForEmployee(historyEmployee.id).map((l) => (
              <div key={l.id} className="hover-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f4f4f4' }}>
                <div>
                  <b style={{ fontSize: '13px' }}>{l.type}</b>
                  <span style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 'bold', background: l.status === 'Approved' ? '#eafff3' : '#fff3cd', color: l.status === 'Approved' ? '#218c61' : '#8a6d3b' }}>{l.status || 'Pending'}</span>
                  <div style={{ fontSize: '12px', color: '#666' }}>{formatDate(l.fromDate)} - {formatDate(l.toDate)} ({l.days} day{l.days > 1 ? 's' : ''})</div>
                  {l.reason && <div style={{ fontSize: '11px', color: '#999' }}>{l.reason}</div>}
                  {l.approvalNote && <div style={{ fontSize: '11px', color: '#218c61' }}>Note: "{l.approvalNote}"</div>}
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {admin && (l.status || 'Pending') === 'Pending' && <button onClick={() => openApprove(l)} style={{ border: 'none', background: '#eafff3', color: '#218c61', padding: '6px', borderRadius: '6px', cursor: 'pointer' }}><Check size={14} /></button>}
                  {admin && <button onClick={() => deleteLeave(l)} style={{ border: 'none', background: '#ffeded', color: 'red', padding: '6px', borderRadius: '6px', cursor: 'pointer' }}><Trash2 size={14} /></button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HR;
